import axios from 'axios';

export default {
  namespaced: true,

  state: () => ({
    workers: [],
    worker: null,          // single worker
    loading: false,
    error: null,
  }),

  getters: {
    allWorkers: state => state.workers,
    totalWorkers: state => state.workers.length,
    activeWorkers: state => state.workers.filter(w => w.active),
    getWorker: state => id => state.workers.find(w => w.id === id),
  },

  mutations: {
    setWorkers(state, data) {
      state.workers = data;
    },

    setWorker(state, worker) {
      state.worker = worker;
    },

    addWorker(state, w) {
      state.workers.push(w);
    },

    updateWorker(state, updated) {
      const i = state.workers.findIndex(w => w.id === updated.id);
      if (i !== -1) state.workers.splice(i, 1, updated);
    },

    removeWorker(state, id) {
      state.workers = state.workers.filter(w => w.id !== id);
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setError(state, error) {
      state.error = error;
    }
  },

  actions: {
    /* -------------------------
       FETCH ALL WORKERS
    --------------------------*/
    async fetchWorkers({ commit }) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const res = await axios.get("/api/workers");
        commit("setWorkers", res.data.data || res.data); // supports both formats
      } catch (err) {
        commit("setError", err?.response?.data?.message || "Failed to load workers");
      } finally {
        commit("setLoading", false);
      }
    },

    /* -------------------------
       FETCH SINGLE WORKER
    --------------------------*/
    async fetchWorker({ commit }, workerId) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const res = await axios.get(`/api/workers/${workerId}`);
        commit("setWorker", res.data.data || res.data);
        return res.data;
      } catch (err) {
        commit("setError", "Worker not found");
        commit("setWorker", null);
        throw err;
      } finally {
        commit("setLoading", false);
      }
    },

    /* -------------------------
       CREATE WORKER
    --------------------------*/
    async addWorker({ commit }, payload) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const res = await axios.post("/api/workers", payload);
        commit("addWorker", res.data.data || res.data);
        return res.data;
      } catch (err) {
        commit("setError", err?.response?.data?.message || "Failed to create worker");
        throw err;
      } finally {
        commit("setLoading", false);
      }
    },

    /* -------------------------
       UPDATE WORKER
    --------------------------*/
    async updateWorkerAction({ commit }, { id, data }) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const res = await axios.put(`/api/workers/${id}`, data);
        commit("updateWorker", res.data.data || res.data);
        return res.data;
      } catch (err) {
        commit("setError", err?.response?.data?.message || "Failed to update worker");
        throw err;
      } finally {
        commit("setLoading", false);
      }
    },

    /* -------------------------
       DELETE WORKER
    --------------------------*/
    async deleteWorker({ commit }, id) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        await axios.delete(`/api/workers/${id}`);
        commit("removeWorker", id);
      } catch (err) {
        commit("setError", err?.response?.data?.message || "Failed to delete worker");
        throw err;
      } finally {
        commit("setLoading", false);
      }
    },

    /* -------------------------
       TOGGLE ACTIVE/INACTIVE
    --------------------------*/
    async toggleWorkerStatus({ state, commit }, id) {
      const worker = state.workers.find(w => w.id === id);
      if (!worker) return;

      const updatedStatus = !worker.active;

      commit("setLoading", true);
      commit("setError", null);

      try {
        const res = await axios.put(`/api/workers/${id}`, {
          active: updatedStatus,
        });

        commit("updateWorker", res.data.data || res.data);
      } catch (err) {
        commit("setError", err?.response?.data?.message || "Failed to toggle status");
      } finally {
        commit("setLoading", false);
      }
    },
  },
};
